// src/pages/Auditorias/components/NovaAuditoriaDialog.js
import React, { useState } from 'react';
import { searchClientsByName, getClientProcesses } from '../../ClientData/api';

/**
 * Nova auditoria: pesquisa o cliente pelo nome, escolhe o processo, dispara.
 *
 * Reutiliza a API de clientes de ClientData — é a mesma DuraLex e o mesmo fluxo
 * que o advogado já conhece.
 *
 * Na v2 este disparo passa a ser automático; o diálogo fica para ajustes e
 * execuções manuais.
 */
const NovaAuditoriaDialog = ({ userEmail, onFechar, onDisparar }) => {
  const [nome, setNome] = useState('');
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);
  const [processos, setProcessos] = useState([]);
  const [processo, setProcesso] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState(null);

  const pesquisar = async (e) => {
    e.preventDefault();
    if (!nome.trim()) return;

    setOcupado(true);
    setErro(null);
    setCliente(null);
    setProcessos([]);
    setProcesso(null);

    try {
      const { data } = await searchClientsByName(nome.trim());
      const lista = data.data?.data || data.data || [];
      setClientes(Array.isArray(lista) ? lista : []);
      if (!lista.length) setErro('Nenhum cliente encontrado com esse nome');
    } catch (e2) {
      setErro(e2.response?.data?.message || 'Erro ao pesquisar clientes');
    } finally {
      setOcupado(false);
    }
  };

  const escolherCliente = async (c) => {
    const codigo = c['Cod. Cliente'] || c.codigo || c.cod_cli;

    setCliente({ ...c, codigo });
    setProcessos([]);
    setProcesso(null);
    setOcupado(true);
    setErro(null);

    try {
      const { data } = await getClientProcesses(codigo);
      const lista = data.data?.data || data.data || [];
      setProcessos(Array.isArray(lista) ? lista : []);
      if (!lista.length) setErro('Este cliente não tem processos');
    } catch (e) {
      setErro(e.response?.data?.message || 'Erro ao carregar processos');
    } finally {
      setOcupado(false);
    }
  };

  const disparar = async () => {
    if (!cliente || !processo) return;

    setOcupado(true);
    setErro(null);

    try {
      await onDisparar({
        cod_cli: String(cliente.codigo),
        cod_processo: processo['Cod. Processo'] || processo.codigo,
        user_email: userEmail,
        modo: 'completa',
        nome_cliente: cliente['Nome'] || cliente.nome || null,
        num_processo: processo['Nº Processo'] || processo.numero || null,
      });
      onFechar();
    } catch (e) {
      setErro(
        e.response?.status === 409
          ? 'Esta auditoria já tem uma execução na fila ou a decorrer'
          : e.response?.data?.message || 'Erro ao disparar a auditoria'
      );
      setOcupado(false);
    }
  };

  return (
    <div className="aud-overlay" onClick={onFechar}>
      <div className="aud-modal aud-modal-pequeno" onClick={(e) => e.stopPropagation()}>
        <div className="aud-modal-cabecalho">
          <h3>Nova auditoria</h3>
          <button className="aud-modal-fechar" onClick={onFechar} aria-label="Fechar">✖</button>
        </div>

        <div className="aud-modal-corpo">
          <form onSubmit={pesquisar} className="aud-form-pesquisa">
            <input
              type="text"
              placeholder="Nome do cliente"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              autoFocus
            />
            <button type="submit" className="aud-btn-secundario" disabled={ocupado || !nome.trim()}>
              Pesquisar
            </button>
          </form>

          {erro && <p className="aud-erro">{erro}</p>}

          {clientes.length > 0 && !cliente && (
            <ul className="aud-lista-escolha">
              {clientes.map((c) => {
                const codigo = c['Cod. Cliente'] || c.codigo || c.cod_cli;
                return (
                  <li key={codigo}>
                    <button type="button" onClick={() => escolherCliente(c)}>
                      <strong>{c['Nome'] || c.nome}</strong>
                      <span className="aud-mono">{codigo}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {cliente && (
            <div className="aud-escolhido">
              <span><strong>{cliente['Nome'] || cliente.nome}</strong> · {cliente.codigo}</span>
              <button type="button" className="aud-link" onClick={() => setCliente(null)}>
                mudar
              </button>
            </div>
          )}

          {processos.length > 0 && (
            <ul className="aud-lista-escolha">
              {processos.map((p) => {
                const codigo = p['Cod. Processo'] || p.codigo;
                const seleccionado = processo && (processo['Cod. Processo'] || processo.codigo) === codigo;
                return (
                  <li key={codigo}>
                    <button
                      type="button"
                      className={seleccionado ? 'seleccionado' : ''}
                      onClick={() => setProcesso(p)}
                    >
                      <strong>{p['Nº Processo'] || p.numero || codigo}</strong>
                      <span className="aud-mono">{codigo}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="aud-modal-rodape">
          <button className="aud-btn-secundario" onClick={onFechar}>CANCELAR</button>
          <button className="aud-btn-primario" onClick={disparar} disabled={!processo || ocupado}>
            {ocupado ? 'A DISPARAR…' : 'AUDITAR PROCESSO'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NovaAuditoriaDialog;
